/* eslint-disable react-hooks/exhaustive-deps */
import { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUndoRedo } from '@anandarizki/use-undo-redo';
import {
  Button,
  Checkbox,
  Dialog,
  FormHelperText,
  FormLabel,
  Group,
  IconUndo,
  Input,
  Loader,
  Select
} from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';

import { Autocomplete } from 'components/autocomplete';
import { useAlert } from 'contexts/alert/useAlert';
import { Metadata } from 'contexts/datamodel/DataModelContext';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import {
  Brand,
  PageType,
  ParametersInput,
  SectionAttributes,
  SectionParameterSchemaEntry,
  useDeletePageMutation,
  useGenerateAllessehIdMutation,
  useGetSectionParameterSchemasQuery
} from 'data/generated/graphql';
import Entitlement from 'features/section-page-edit/components/page-draft-section/components/page-module-modal/components/entitlementsManager/EntitlementsManager';
import { useDebounce } from 'hooks';
import { useIsAdmin, useIsSuper } from 'hooks/roles';
import { useAdPlacementTypeValues } from 'hooks/useAdPlacementTypeValues';
import { useAdZoneValues } from 'hooks/useAdZoneValues';
import useIsNewSectionPage from 'hooks/useIsNewSectionPage';
import { useWsjAdSectionKeyValues } from 'hooks/useWsjAdSectionKeyValues';
import SectionParameterField from './SectionParameterField';
import styles from '../../artifact-settings.module.scss';

// applies to WSJ mobile app
const AD_CONFIG_PUBLICATIONS = ['wsj', 'jwsj', 'wsjopinion', 'buyside'];

const EXCLUDE_FROM_LEGACY_PUBLICATIONS = ['wsj', 'jwsj', 'wsjopinion'];

type SectionInfo = {
  sectionAttributes: SectionAttributes;
  metadata: Metadata;
};
interface PageParametersFormProps {
  sectionAttributesInitial: SectionAttributes;
  metadataInitial: Metadata;
  publicationKey: string;
  actions: {
    update: (info: SectionInfo) => void;
  };
  isReadOnly?: boolean;
}

const PageParametersForm: FC<PageParametersFormProps> = ({
  actions,
  sectionAttributesInitial,
  metadataInitial,
  publicationKey,
  isReadOnly = false
}) => {
  const { metadata: contextMetadata, getSectionAttributes } = useDataModelContext();
  const [sectionInfo, setSectionInfo] = useState<SectionInfo>(() => ({
    metadata: metadataInitial,
    sectionAttributes: sectionAttributesInitial
  }));
  const queryClient = useQueryClient();
  const [deleteDialogStatus, setDeleteDialogStatus] = useState<'open' | 'closed'>('closed');
  const [deletePageConfirmInputText, setDeletePageConfirmInputText] = useState('');

  const deletePageMutation = useDeletePageMutation();

  const { alertError, alertSuccess } = useAlert();

  const navigate = useNavigate();

  const debouncedName = useDebounce(contextMetadata.name, 1000);

  const generateAllessehIdMutation = useGenerateAllessehIdMutation();

  const isCreatingNewPage = useIsNewSectionPage();

  const [undo, , { canUndo }] = useUndoRedo([sectionInfo, setSectionInfo], {
    debounce: 500
  });

  const isAdmin = useIsAdmin();
  const isSuper = useIsSuper();
  const isInitialMount = useRef(true);
  const { adPlacementTypeValues } = useAdPlacementTypeValues();
  const { adZoneValues } = useAdZoneValues();
  const { sectionKeyValues } = useWsjAdSectionKeyValues();

  const { data: sectionParamSchemasData } = useGetSectionParameterSchemasQuery(
    { publicationKey },
    { enabled: !!publicationKey }
  );
  const sectionParamSchemas: SectionParameterSchemaEntry[] = sectionParamSchemasData?.getSectionParameterSchemas ?? [];

  const getSectionParamValue = (key: string): string =>
    sectionInfo.sectionAttributes.sectionParameters?.find((p) => p.name === key)?.value ?? '';

  const handleSectionParamChange = (key: string, value: string) => {
    const existing = getSectionAttributes().sectionParameters ?? [];
    const updated: ParametersInput[] = existing.some((p) => p.name === key)
      ? existing.map((p) => (p.name === key ? { ...p, value } : p))
      : [...existing, { name: key, value, type: 'parameter' }];
    setSectionInfo((prev) => ({
      ...prev,
      sectionAttributes: { ...prev.sectionAttributes, sectionParameters: updated }
    }));
  };

  // Sync only the name field from context metadata changes (e.g., from PageTitle component)
  // This allows the name to be updated in both new and existing pages
  useEffect(() => {
    setSectionInfo((prev) => {
      // Only update if the name actually changed to prevent render loops
      if (prev.metadata.name !== contextMetadata.name) {
        return {
          ...prev,
          metadata: {
            ...prev.metadata,
            name: contextMetadata.name
          }
        };
      }
      return prev;
    });
  }, [contextMetadata.name]);

  useEffect(() => {
    if (
      typeof debouncedName !== 'string' ||
      debouncedName.trim().length === 0 ||
      debouncedName.trim() === '[Untitled]' ||
      !isCreatingNewPage
    ) {
      return;
    }

    generateAllessehIdMutation.mutate(
      {
        generateAllessehIdInput: {
          name: debouncedName.trim(),
          publicationKey
        }
      },
      {
        onSuccess: (data) => {
          const { generateAllessehId: allessehId } = data;
          setSectionInfo((prev) => ({
            ...prev,
            sectionAttributes: {
              ...prev.sectionAttributes,
              routeKey: `/${debouncedName.trim().toLocaleLowerCase().split(' ').join('-')}`,
              sectionLabel: debouncedName.trim()
            },
            metadata: {
              ...prev.metadata,
              allessehId
            }
          }));
        },
        onError: (error) => {
          // Handle other errors normally
          let errorMessage = 'Could not generate Allesseh ID.';

          if (error instanceof Error) {
            errorMessage = error.message;
          }

          alertError(errorMessage);
        }
      }
    );
  }, [debouncedName]);

  const showAdSettings = AD_CONFIG_PUBLICATIONS.includes(publicationKey);
  const showExcludeFromLegacy = EXCLUDE_FROM_LEGACY_PUBLICATIONS.includes(publicationKey);

  const changeEvent =
    <T extends HTMLInputElement | HTMLSelectElement>(name: keyof SectionAttributes) =>
    (e: React.ChangeEvent<T>) => {
      let value: unknown = null;

      value = e.target.value;

      if (e.target.type === 'checkbox') {
        const { checked } = e.target as HTMLInputElement;

        value = checked;
      }

      // Transform NONE to null for sectionKey
      if (name === 'sectionKey' && value === 'NONE') {
        value = null;
      }

      setSectionInfo((prev) => ({
        ...prev,
        sectionAttributes: {
          ...prev.sectionAttributes,
          [name]: value
        }
      }));
    };

  const handleMetadataChange = (field: keyof Metadata) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setSectionInfo((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [field]: value
      }
    }));
  };

  const handleAdPlacementTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value || '';
    setSectionInfo((prev) => ({
      ...prev,
      sectionAttributes: {
        ...prev.sectionAttributes,
        adPlacementType: newValue
      }
    }));
  };

  const handleAdZoneChange = (value: string) => {
    setSectionInfo((prev) => ({
      ...prev,
      sectionAttributes: {
        ...prev.sectionAttributes,
        adZone: value
      }
    }));
  };

  useEffect(() => {
    // Skip calling actions.update on initial mount to prevent render loops
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    actions.update(sectionInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionInfo.metadata, sectionInfo.sectionAttributes]);

  const deletePage = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();

    deletePageMutation.mutate(
      {
        pageDeleteInput: {
          allessehId: sectionInfo.metadata.allessehId,
          publicationKey
        }
      },
      {
        onError: (error) => {
          let errorMessage = 'Could not delete page.';

          if (error instanceof Error) {
            errorMessage = error.message;
          }

          alertError(errorMessage);
        },
        onSuccess: async (response) => {
          if (!response.deletePage.success) {
            alertError(response.deletePage.errorMessage);

            return;
          }

          await queryClient.invalidateQueries([
            'PagesDTO.infinite',
            {
              publicationKey
            }
          ]);

          setDeleteDialogStatus('closed');

          alertSuccess('Page deleted successfully.');

          navigate(`/${publicationKey}/`);
        }
      }
    );
  };

  return (
    <Group gap="md" direction="column">
      <Group.Item align="flex-end">
        <Button
          iconPosition="left"
          icon={IconUndo}
          disabled={!canUndo || isReadOnly}
          onClick={(e: SyntheticEvent<HTMLButtonElement>) => {
            e.preventDefault();
            undo();
          }}
          secondary
        >
          Undo
        </Button>
      </Group.Item>
      <Group.Item fullWidth>
        <Group gap="xs" direction="column" fullWidth>
          <FormLabel label="Allesseh ID" labelPosition="top" fullWidth />
          <Input
            type="text"
            name="allessehId"
            disabled={
              isReadOnly ||
              !isCreatingNewPage ||
              sectionInfo.metadata.name.trim().length === 0 ||
              sectionInfo.metadata.name === '[Untitled]' ||
              generateAllessehIdMutation.isLoading
            }
            value={sectionInfo.metadata.allessehId}
            onChange={handleMetadataChange('allessehId')}
          />
        </Group>
      </Group.Item>
      <Group.Item fullWidth>
        <Group gap="xs" direction="column" fullWidth>
          <FormLabel label="Subtitle" labelPosition="top" fullWidth />
          <Input
            type="text"
            name="subtitle"
            value={sectionInfo.sectionAttributes.subtitle ?? ''}
            onChange={changeEvent<HTMLInputElement>('subtitle')}
            disabled={isReadOnly}
          />
        </Group>
      </Group.Item>
      <Group.Item fullWidth>
        <Group gap="xs" direction="column" fullWidth>
          <FormLabel label="Page Type" labelPosition="top" fullWidth />
          <Select
            name="pageType"
            value={sectionInfo.sectionAttributes.pageType}
            onChange={changeEvent<HTMLSelectElement>('pageType')}
            disabled
          >
            {Object.values(PageType).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Select>
        </Group>
      </Group.Item>
      <Group.Item fullWidth>
        <Group gap="xs" direction="column" fullWidth>
          <FormLabel label="Route key" labelPosition="top" fullWidth />
          <Input
            type="text"
            name="routeKey"
            value={sectionInfo.sectionAttributes.routeKey}
            onChange={changeEvent<HTMLInputElement>('routeKey')}
            disabled={(!isAdmin && !isSuper) || isReadOnly}
          />
        </Group>
      </Group.Item>
      <Group.Item fullWidth>
        <Group gap="xs" direction="column" fullWidth>
          <FormLabel label="Summary priority" labelPosition="top" fullWidth />
          <Input
            type="text"
            name="summaryPriority"
            value={sectionInfo.sectionAttributes.summaryPriority}
            onChange={changeEvent<HTMLInputElement>('summaryPriority')}
            disabled={(!isSuper && !isAdmin) || isReadOnly}
          />
        </Group>
      </Group.Item>
      {showAdSettings && (
        <Group.Item fullWidth>
          <Group gap="xs" direction="column" fullWidth>
            <FormLabel label="Section key" labelPosition="top" fullWidth />
            <Select
              name="Section Key"
              value={sectionInfo.sectionAttributes.sectionKey ?? 'NONE'}
              onChange={changeEvent<HTMLSelectElement>('sectionKey')}
              disabled={!isAdmin || isReadOnly}
            >
              {sectionKeyValues.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </Group>
        </Group.Item>
      )}
      <Group.Item fullWidth>
        <Group gap="xs" direction="column" fullWidth>
          <FormLabel label="Brand" labelPosition="top" fullWidth />
          <Select
            name="brand"
            value={sectionInfo.sectionAttributes.brand}
            onChange={changeEvent<HTMLSelectElement>('brand')}
            disabled={(!isAdmin && !isSuper) || isReadOnly}
          >
            {Object.values(Brand).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Select>
        </Group>
      </Group.Item>
      <Group.Item fullWidth>
        <Group gap="xs" direction="column" fullWidth>
          <FormLabel label="Section label" labelPosition="top" fullWidth />
          <Input
            type="text"
            name="sectionLabel"
            value={sectionInfo.sectionAttributes.sectionLabel}
            onChange={changeEvent<HTMLInputElement>('sectionLabel')}
            disabled={(!isAdmin && !isSuper) || isReadOnly}
          />
        </Group>
      </Group.Item>
      <Group.Item fullWidth>
        <Group gap="xs" direction="column" fullWidth>
          <FormLabel label="Publication key" labelPosition="top" fullWidth />
          <Input type="text" disabled name="publicationKey" defaultValue={publicationKey} />
        </Group>
      </Group.Item>
      {showAdSettings && (
        <Group.Item fullWidth>
          <Group gap="xs" direction="column" fullWidth>
            <FormLabel label="Ad Placement Type" labelPosition="top" fullWidth required />
            <Select
              name="adPlacementType"
              value={sectionInfo.sectionAttributes.adPlacementType ?? ''}
              onChange={handleAdPlacementTypeChange}
              disabled={(!isSuper && !isAdmin) || isReadOnly}
              aria-required="true"
              error={!sectionInfo.sectionAttributes.adPlacementType}
            >
              {!sectionInfo.sectionAttributes.adPlacementType && (
                <option value="">-- Select Ad Placement Type --</option>
              )}
              {adPlacementTypeValues.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
            <FormHelperText>Select the type where the Ad is placed.</FormHelperText>
          </Group>
        </Group.Item>
      )}
      {showAdSettings &&
        (() => {
          const { adPlacementType } = sectionInfo.sectionAttributes;
          // Convert to string and normalize for comparison (handles both enum values and string values)
          const adPlacementTypeStr = adPlacementType ? String(adPlacementType).toLowerCase() : null;
          const isHidden = adPlacementTypeStr === 'hidden';
          const isAdZoneRequired = adPlacementType && !isHidden;
          const isAdZoneDisabled = (!isSuper && !isAdmin) || !adPlacementType || isHidden || isReadOnly;
          const adZoneValue = sectionInfo.sectionAttributes.adZone ?? '';

          return (
            <Group.Item fullWidth>
              <Group gap="xs" direction="column" fullWidth>
                <FormLabel label="Ad Zone" labelPosition="top" fullWidth required={!!isAdZoneRequired} />
                <Autocomplete<string>
                  value={adZoneValue}
                  onChange={handleAdZoneChange}
                  options={adZoneValues}
                  disabled={isAdZoneDisabled}
                  error={!!(isAdZoneRequired && adPlacementType && !adZoneValue)}
                  placeholder="Enter ad zone"
                  required={!!isAdZoneRequired}
                  maxOptions={10}
                />
                <FormHelperText>
                  {isAdZoneDisabled && isHidden
                    ? 'Ad Zone is disabled when Ad Placement Type is set to Hidden'
                    : 'Enter a zone for Ad metrics'}
                </FormHelperText>
              </Group>
            </Group.Item>
          );
        })()}
      {(isAdmin || isSuper) && (
        <Group.Item fullWidth>
          <Group gap="xs" direction="column" fullWidth>
            <FormLabel label="A/B Test ID" labelPosition="top" fullWidth />
            <Input
              type="text"
              name="abTestId"
              value={sectionInfo.sectionAttributes.abTestId ?? ''}
              onChange={changeEvent<HTMLInputElement>('abTestId')}
            />
          </Group>
        </Group.Item>
      )}
      {(isAdmin || isSuper) && (
        <Group.Item fullWidth>
          <Entitlement
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            value={(sectionInfo.sectionAttributes.entitlements ?? []) as string[]}
            onChange={(entitlements: string[]) =>
              setSectionInfo((prev) => ({
                ...prev,
                sectionAttributes: {
                  ...prev.sectionAttributes,
                  entitlements
                }
              }))
            }
            staticPlacement
            isReadOnly={isReadOnly}
          />
        </Group.Item>
      )}
      {showExcludeFromLegacy && (
        <FormLabel label="Exclude section from the Legacy Mobile App" labelPosition="right" fullWidth>
          <Checkbox
            checked={sectionInfo.sectionAttributes.excludeFromLegacy}
            name="excludeFromLegacy"
            disabled={!isAdmin || isReadOnly}
            onChange={changeEvent<HTMLInputElement>('excludeFromLegacy')}
          />
        </FormLabel>
      )}

      {sectionParamSchemas
        .filter((s) => s.userExposed !== true)
        .map((schema) => (
          <SectionParameterField
            key={schema.key}
            schema={schema}
            currentValue={getSectionParamValue(schema.key)}
            isReadOnly={isReadOnly}
            onChange={handleSectionParamChange}
          />
        ))}

      {isAdmin && (
        <Group.Item fullWidth>
          <Group gap="xs" direction="column" fullWidth>
            <FormLabel label="Danger Zone" labelPosition="top" fullWidth />
            <Button
              className={styles.deleteButton}
              margin={{
                top: 'md'
              }}
              primary
              color="lava"
              disabled={isReadOnly}
              onClick={(e: SyntheticEvent<HTMLButtonElement>) => {
                e.preventDefault();

                setDeleteDialogStatus('open');
              }}
            >
              Delete Page
            </Button>
          </Group>
        </Group.Item>
      )}

      <Dialog
        aria-labelledby="dialogTitle"
        aria-describedby="dialogDesc"
        status={deleteDialogStatus}
        onDismiss={() => setDeleteDialogStatus('closed')}
      >
        <Dialog.Title id="dialogTitle">Delete Page</Dialog.Title>
        <Dialog.Content id="dialogDesc" data-testid="confirmation-msg">
          <div>Enter the allesseh ID of this page to delete it</div>
          <Input
            value={deletePageConfirmInputText}
            type="text"
            name="allesseh ID"
            placeholder="Allesseh ID"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDeletePageConfirmInputText(e.target.value);
            }}
            margin={{
              top: 'md'
            }}
          />
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            margin={{
              top: 'md'
            }}
            secondary
            onClick={() => {
              setDeleteDialogStatus('closed');
            }}
          >
            Cancel
          </Button>
          <Button
            className={styles.deleteButton}
            margin={{
              top: 'md'
            }}
            primary
            disabled={!isAdmin || isReadOnly || deletePageConfirmInputText !== sectionInfo.metadata.allessehId}
            onClick={deletePage}
          >
            {deletePageMutation.isLoading ? <Loader size="md" /> : 'Delete page'}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Group>
  );
};

export default PageParametersForm;
